import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useRouter } from "../../store/router";
import { useI18n } from "../../i18n/useI18n";
import { useBranding } from "../../hooks/useBranding";
import { useStyles, useThemes, themeThumbnail } from "../../hooks/useStyles";
import { Button } from "../../components/ui/Button";
import { Lines } from "../../components/ui/Lines";

const POSITIONS = ["left", "center", "right"] as const;

/** 카드 한 장이 다음 이미지로 넘어가는 주기(ms). 세 장이 순서대로 바뀌므로 한 장당 3배 주기. */
const ROTATE_INTERVAL_MS = 3800;

/** 부채에 올릴 이미지 한 장 — 테마 대표 썸네일(없으면 스타일 썸네일) */
interface FanImage {
  id: string;
  src: string;
}

/**
 * STAGE · Attract(웰컴, HERIT 00) — 부채처럼 펼친 사진 3장(가운데 세로 · 양옆 ±10°) 위에
 * 태그라인 · 헤드라인 · 시작하기. 사진은 진입 시 한 장씩 내려앉고(CSS), 이후 은은하게 부유하며
 * 몇 초마다 한 장씩 다른 테마로 크로스페이드된다. 매니저 브랜딩이 이미지 모드면 부채를 이미지로 대체.
 */
export function AttractView() {
  const go = useRouter((s) => s.go);
  const setStyle = useRouter((s) => s.setStyle);
  const setTheme = useRouter((s) => s.setTheme);
  const { t } = useI18n();
  const { wordmark, title, subtitle, imageUrl } = useBranding();
  const images = useFanImages();
  const cards = useRotatingCards(images);

  const start = () => {
    setTheme(null);
    setStyle(null);
    go("themeSelect");
  };

  return (
    <div className="view view--stackable">
      <section className="attract">
        {imageUrl ? (
          <div className="attract__image attract__image--animate">
            <img src={imageUrl} alt={wordmark} draggable={false} />
          </div>
        ) : (
          <div className="fan" aria-hidden>
            {cards.map((img, i) => {
              const pos = POSITIONS[i]!;
              return (
                <figure key={pos} className={`fan-card fan-card--${pos}`}>
                  <div className="fan-card__float">
                    <div className="pic">
                      <AnimatePresence initial={false}>
                        <motion.img
                          key={img.id}
                          src={img.src}
                          alt=""
                          draggable={false}
                          className="fan-card__img"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.9, ease: "easeInOut" }}
                        />
                      </AnimatePresence>
                    </div>
                  </div>
                </figure>
              );
            })}
          </div>
        )}

        <div className="attract__copy attract__copy--animate">
          {/* subtitle/title 은 매니저 브랜딩 오버라이드 → 없으면 i18n 태그라인/헤드라인 */}
          <p className="tagline">{subtitle}</p>
          <h1><Lines text={title} /></h1>
          <Button lg onClick={start}>{t.attract.cta}</Button>
        </div>
      </section>
    </div>
  );
}

/** 부채 이미지 후보 — 썸네일이 있는 테마 우선, 부족하면 스타일 썸네일로 채운다(중복 src 제외). */
function useFanImages(): FanImage[] {
  const themes = useThemes();
  const styles = useStyles();
  return useMemo(() => {
    const out: FanImage[] = [];
    const seen = new Set<string>();
    for (const th of themes) {
      const src = themeThumbnail(th, styles);
      if (src && !seen.has(src)) {
        seen.add(src);
        out.push({ id: `theme:${th.id}`, src });
      }
    }
    if (out.length < POSITIONS.length) {
      for (const s of styles) {
        if (s.thumbnailUrl && !seen.has(s.thumbnailUrl)) {
          seen.add(s.thumbnailUrl);
          out.push({ id: `style:${s.id}`, src: s.thumbnailUrl });
        }
      }
    }
    return out;
  }, [themes, styles]);
}

/** 초기 배치: 가운데에 첫 이미지, 좌우에 2·3번째. 이미지가 3장 미만이면 첫 장을 반복. */
function initialIndices(count: number): number[] {
  if (count === 0) return [];
  const pick = (i: number) => (i < count ? i : 0);
  return [pick(1), 0, pick(2)];
}

/**
 * 세 슬롯(left/center/right)에 보여줄 이미지를 주기적으로 한 슬롯씩 갈아끼운다.
 * 현재 화면에 없는 이미지 중 다음 순번을 골라 넣어 세 장이 서로 겹치지 않게 유지.
 * 이미지가 3장 이하면 순환하지 않는다. 모션 감소 선호 시 정적.
 */
function useRotatingCards(images: readonly FanImage[]): FanImage[] {
  const [indices, setIndices] = useState<number[]>(() => initialIndices(images.length));
  // 인터벌 콜백이 최신 배치를 읽기 위한 ref (StrictMode 의 updater 중복 호출을 피하려고 updater 밖에서 계산)
  const indicesRef = useRef(indices);
  indicesRef.current = indices;

  useEffect(() => {
    setIndices(initialIndices(images.length));
  }, [images]);

  useEffect(() => {
    if (images.length <= POSITIONS.length) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    const order: number[] = [1, 0, 2]; // 가운데 → 왼쪽 → 오른쪽 순으로 갈아끼움
    let step = 0;
    let cursor = POSITIONS.length; // 아직 안 보인 이미지부터
    const id = window.setInterval(() => {
      const prev = indicesRef.current;
      if (prev.length !== POSITIONS.length) return;
      let next = cursor % images.length;
      for (let tries = 0; tries < images.length && prev.includes(next); tries++) {
        next = (next + 1) % images.length;
      }
      cursor = next + 1;
      const target = order[step % order.length]!;
      step += 1;
      const copy = prev.slice();
      copy[target] = next;
      setIndices(copy);
    }, ROTATE_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [images]);

  return indices.map((i) => images[i] ?? images[0]!).filter(Boolean);
}
